"use client";
import { useEffect } from "react";
import { PricingTable, useUser } from "@clerk/nextjs";
import { useTokenBudgetStore } from "@/stores/tokenBudgetStore";
import { IconBolt } from "@tabler/icons-react";
import styles from "./styles.module.scss";
import { Box as MantineBox, Text as MantineText } from "@mantine/core";

declare global {
  interface Window {
    Clerk?: {
      openSignUp: () => void;
    };
  }
}

export const TokenUsageBadge = () => {
  const initialized = useTokenBudgetStore((s) => s.initialized);
  const tokensUsed = useTokenBudgetStore((s) => s.tokensUsed);
  const tokensLimit = useTokenBudgetStore((s) => s.tokensLimit);
  const tier = useTokenBudgetStore((s) => s.tier);
  const resetsAt = useTokenBudgetStore((s) => s.resetsAt);

  if (!initialized) return null;

  const pct = tokensLimit > 0 ? Math.round((tokensUsed / tokensLimit) * 100) : 0;
  const resetTime = resetsAt
    ? new Date(resetsAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <MantineBox className={styles.card}>
      <MantineBox className={styles.header}>
        <IconBolt size={16} />
        <MantineText span className={styles.headerLabel}>
          Your daily token usage
        </MantineText>
        <MantineText span className={styles.headerTier}>
          {tier} plan
        </MantineText>
      </MantineBox>
      <MantineBox className={styles.track}>
        <MantineBox
          className={`${styles.fill} ${pct >= 90 ? styles.fillDanger : styles.fillNormal}`}
          style={{ width: `${pct}%` }}
        />
      </MantineBox>
      <MantineBox className={styles.footer}>
        <MantineText span>
          {tokensUsed} / {tokensLimit} tokens used today
        </MantineText>
        {resetTime && <MantineText span>Resets {resetTime}</MantineText>}
      </MantineBox>
    </MantineBox>
  );
};

const PlansTable = () => {
  const { isSignedIn } = useUser();

  useEffect(() => {
    const observer = new MutationObserver(() => {
      let updated = false;

      const updateCardText = (cardClass: string, buttonText: string) => {
        const card = document.querySelector(cardClass);
        const button = card?.querySelector(".cl-pricingTableCardFooterButton");
        if (button) {
          button.textContent = buttonText;
          // Only intercept clicks for non-signed-in users
          if (!isSignedIn) {
            const btn = button as HTMLElement;
            btn.onclick = (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              window.Clerk?.openSignUp();
            };
            if (btn.tagName === "A") {
              btn.removeAttribute("href");
            }
          }
          updated = true;
        }
      };

      const updateEnterpriseCard = () => {
        const card = document.querySelector(".cl-pricingTableCard__kurb_enterprise");
        if (!card) return;

        const button = card.querySelector(".cl-pricingTableCardFooterButton");
        const period = card.querySelector(".cl-pricingTableCardFeePeriod");
        const periodNotice = card.querySelector(".cl-pricingTableCardFeePeriodNotice");
        const priceLabel = card.querySelector(".cl-pricingTableCardFee");

        if (button) {
          const link = document.createElement("a");
          link.textContent = "Get in touch";
          link.href = "mailto:contact@kurb.online";
          link.target = "_blank";
          link.className = button.className;
          button.replaceWith(link);
          updated = true;
        }

        if (priceLabel) {
          priceLabel.textContent = "Custom pricing";
          updated = true;
        }

        [period, periodNotice].forEach((el) => {
          if (el?.parentNode) {
            el.parentNode.removeChild(el);
            updated = true;
          }
        });
      };

      updateCardText(".cl-pricingTableCard__pro", "Start free trial");
      updateCardText(".cl-pricingTableCard__free_user", "Start");
      updateEnterpriseCard();

      // Handle any remaining buttons (like SUBSCRIBE) to open modal for non-signed-in users only
      if (!isSignedIn) {
        const allButtons = document.querySelectorAll(".cl-pricingTableCardFooterButton");
        allButtons.forEach((button) => {
          if (!button.closest(".cl-pricingTableCard__kurb_enterprise")) {
            const btn = button as HTMLElement;
            btn.onclick = (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              window.Clerk?.openSignUp();
            };
            if (btn.tagName === "A") {
              btn.removeAttribute("href");
            }
          }
        });
      }

      if (updated) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isSignedIn]);

  return <PricingTable ctaPosition="top" />;
};

export default PlansTable;
